'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { signIn } from 'next-auth/react';
import { AUTH_PROVIDER } from '@/constants/auth';
import { Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useResolvedColorScheme } from '@/hooks/useResolvedColorScheme';
import { COLOR_SCHEME } from '@/constants/colorScheme';
import { useAdminGuard } from '@/hooks/useAdminGuard';
import { usePrivate } from '@/components/private';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  styled,
  Toolbar,
} from '@/primitives';
import { UserAvatar } from '@/components/avatars/UserAvatar';
import {
  APP_ROUTE_NAMES,
  APP_ROUTE_PATHS,
  PUBLIC_ROUTES,
} from '@/configs/routes';
import { useIsPublicSearch } from '@/hooks/useIsPublicSearch';
import { useIsRegistryRoute } from '@/hooks/useIsRegistryRoute';
import { NAV_HEIGHT } from '@/configs/layout';
import SocietySearchHeaderCenterDesktop from '@/components/publicSearch/searchPage/SocietySearchHeaderCenterDesktop';
import SocietySearchHeaderLogo from '@/components/publicSearch/searchPage/SocietySearchHeaderLogo';
const HEADER_TRANSITION_MS = 280;

const LinkStyled = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

const navLinkStackSx = {
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: { md: 'center' },
  gap: { xs: '6px', md: '8px' },
  width: { xs: '100%', md: 'fit-content' },
  '& .MuiButton-root': {
    textTransform: 'none',
    fontSize: { xs: '12px', md: '12px' },
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  },
  a: {
    justifyContent: { xs: 'start', md: 'center' },
    padding: { xs: '4px 6px', md: '6px 16px' },
    minHeight: { xs: '30px', md: 0 },
    height: { xs: 'auto', md: 'auto' },
    width: 'fit-content',
    minWidth: 0,
    fontSize: { xs: '12px', md: '12px' },
    lineHeight: { xs: 1.3, md: 1.5 },
    fontWeight: 600,
  },
} as const;

type NavigationLayout = 'desktop' | 'mobile';

interface Props {
  /** Passed by the toolbar wrapper — avoids useMediaQuery SSR default (false)
      flashing mobile nav links left-aligned inside the desktop grid. */
  layout?: NavigationLayout;
  onNavigate?: () => void;
  onClose?: () => void;
}

function HeaderNavLinkStack({
  pathname,
  isPublicSearch,
  isRegistryRoute,
  onNavigate,
}: {
  pathname: string;
  isPublicSearch: boolean;
  isRegistryRoute: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Stack direction="row" sx={navLinkStackSx}>
      <Button
        component={LinkStyled}
        href={PUBLIC_ROUTES.HOME}
        sx={{
          color:
            pathname === PUBLIC_ROUTES.HOME ? 'primary.main' : 'text.secondary',
        }}
        onClick={onNavigate}
      >
        {APP_ROUTE_NAMES.HOME}
      </Button>
      <Button
        component={LinkStyled}
        href={PUBLIC_ROUTES.SEARCH}
        sx={{
          color:
            pathname === PUBLIC_ROUTES.SEARCH || isPublicSearch
              ? 'primary.main'
              : 'text.secondary',
        }}
        onClick={onNavigate}
      >
        {APP_ROUTE_NAMES.SEARCH}
      </Button>
      <Button
        component={LinkStyled}
        href={PUBLIC_ROUTES.REGISTRY_LANDING}
        sx={{
          color: isRegistryRoute ? 'primary.main' : 'text.secondary',
        }}
        onClick={onNavigate}
      >
        {APP_ROUTE_NAMES.REGISTRY}
      </Button>
    </Stack>
  );
}

export default function Navigation({
  layout = 'mobile',
  onNavigate,
  onClose,
}: Props) {
  const { resolvedScheme } = useResolvedColorScheme();
  const isMdUp = layout === 'desktop';
  const isPublicSearch = useIsPublicSearch();
  const isRegistryRoute = useIsRegistryRoute();
  const pathname = usePathname();
  const { isAuthenticated, isAuthLoading } = useAdminGuard(
    APP_ROUTE_PATHS.DASHBOARD,
  );
  const { userProfile } = usePrivate();
  // After auth resolves, there's a second request that fetches the user
  // profile (PostLoginFlow → /api/UserProfile/:id). Until that lands the
  // avatar has no name or image, so show the spinner during that window
  // too — not just during the session check.
  const isAuthenticatedAwaitingProfile = isAuthenticated && !userProfile;

  const centerSlideKey = isPublicSearch
    ? 'header-center-society'
    : 'header-center-default';

  const breedlogicLogoSrc =
    resolvedScheme === COLOR_SCHEME.DARK
      ? '/breedlogic-logo-dark.svg'
      : '/breedlogic-logo.svg';
  return (
    <Toolbar
      component="nav"
      disableGutters
      sx={{
        flexDirection: { xs: 'column', md: 'row' },
        display: { xs: 'flex', md: 'grid' },
        // `auto minmax(0, 1fr) auto`: the flanking columns size to their own
        // content (logo + society logo on the left, auth slot on the right) so
        // they're never encroached on, and the centre column takes the
        // remaining space as a *definite* width. A content-sized `auto` centre
        // (the previous `1fr auto 1fr`) let the tabs grow until they overlapped
        // the society logo, and starved the tab row's overflow logic — it
        // measures the centre column to decide how many tabs fit, so a
        // content-sized column always reported "everything fits" and the "More"
        // menu never engaged. A flexible centre clips via overflow:hidden and
        // gives that logic the true available width.
        gridTemplateColumns: { md: 'auto minmax(0, 1fr) auto' },
        alignItems: { xs: 'start', md: 'center' },
        columnGap: { md: '16px' },
        gap: { xs: '10px', md: 0 },
        px: { xs: 0, md: '24px' },
        py: { md: 0 },
        width: { md: '100%' },
        height: { md: '100%' },
        minHeight: { xs: 'auto', md: `${NAV_HEIGHT}px` },
        boxSizing: 'border-box',
        // Pure-CSS entrance — slides + fades the nav contents in from below
        // on first paint. The surrounding AppBar (background/border) stays
        // static and clips this with overflow:hidden so the contents appear
        // to rise into the bar from beneath it.
        animation: 'bl-fade-in 0.45s cubic-bezier(0.22, 1, 0.36, 1) both',
      }}
    >
      {isMdUp ? (
        <Stack
          direction="row"
          alignItems="center"
          gap="12px"
          sx={{ flexShrink: 0, minWidth: 0, justifySelf: 'start' }}
        >
          <LinkStyled
            href={PUBLIC_ROUTES.HOME}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              width: 112,
              height: 36,
              padding: 0,
            }}
          >
            <Image
              alt="BreedLOGIC Logo"
              height={37}
              width={176}
              priority
              src={breedlogicLogoSrc}
              style={{
                objectFit: 'contain',
                width: '100%',
                height: '100%',
              }}
            />
          </LinkStyled>
          {isPublicSearch ? <SocietySearchHeaderLogo /> : null}
        </Stack>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <LinkStyled
            href={PUBLIC_ROUTES.HOME}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              width: 96,
              height: 30,
              padding: 0,
            }}
          >
            <Image
              alt="BreedLOGIC Logo"
              height={37}
              width={176}
              priority
              src={breedlogicLogoSrc}
              style={{
                objectFit: 'contain',
                width: '100%',
                height: '100%',
              }}
            />
          </LinkStyled>
          {onClose && (
            <IconButton aria-label="Close menu" size="small" onClick={onClose}>
              <CloseIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
            </IconButton>
          )}
        </Box>
      )}

      {isMdUp ? (
        <Box
          sx={{
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: '4px',
            overflow: 'hidden',
          }}
        >
          {/* `appear={false}` is essential here — MUI Slide defaults appear
              to true. With it on, SSR rendered the menu in its final position
              and JS then snapped it off-screen before animating in (visible as
              a flash). With appear={false}, the entrance is omitted on first
              mount; the transition still plays on key changes (header swaps). */}
          <Slide
            appear={false}
            direction={isPublicSearch ? 'down' : 'up'}
            timeout={HEADER_TRANSITION_MS}
            in
            key={centerSlideKey}
          >
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {isPublicSearch ? (
                <SocietySearchHeaderCenterDesktop />
              ) : (
                <HeaderNavLinkStack
                  pathname={pathname}
                  isPublicSearch={isPublicSearch}
                  isRegistryRoute={isRegistryRoute}
                  onNavigate={onNavigate}
                />
              )}
            </Box>
          </Slide>
        </Box>
      ) : (
        <Slide
          appear={false}
          direction={isPublicSearch ? 'down' : 'up'}
          timeout={HEADER_TRANSITION_MS}
          in
          key={
            isPublicSearch ? 'header-mobile-society' : 'header-mobile-default'
          }
        >
          <Box sx={{ width: '100%' }}>
            <HeaderNavLinkStack
              pathname={pathname}
              isPublicSearch={isPublicSearch}
              isRegistryRoute={isRegistryRoute}
              onNavigate={onNavigate}
            />
          </Box>
        </Slide>
      )}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent={{ md: 'flex-end' }}
        gap={{ xs: 1, md: 1.5 }}
        sx={{
          flexShrink: 0,
          width: { xs: '100%', md: 'auto' },
          justifySelf: { md: 'end' },
        }}
      >
        {/* Three states for the auth/profile slot:
            1. `isAuthLoading` — session is still being resolved. Empty
               placeholder so we don't briefly flash a Sign-in button at a
               user who turns out to be authenticated.
            2. `isAuthenticatedAwaitingProfile` — session resolved, but
               PostLoginFlow's profile request hasn't landed yet, so the
               avatar would render with no name or image. Show a spinner.
            3. Settled: either UserAvatar (with name/image) or the Sign-in
               button. Width matches across all three so the toolbar
               layout doesn't shift. */}
        {isAuthLoading ? (
          <Box
            aria-hidden
            sx={{
              minHeight: { xs: '32px', md: '40px' },
              height: { xs: '32px', md: '40px' },
              width: { xs: '100%', md: 'auto' },
              minWidth: { xs: '100%', md: '160px' },
            }}
          />
        ) : isAuthenticatedAwaitingProfile ? (
          <Box
            aria-label="Loading user profile"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '4px',
              padding: { xs: '4px 5px', md: '5px 6px' },
              minHeight: { xs: '32px', md: '40px' },
              height: { xs: '32px', md: '40px' },
              width: { xs: '100%', md: 'auto' },
              minWidth: { xs: '100%', md: '160px' },
            }}
          >
            <CircularProgress size={18} thickness={4} />
          </Box>
        ) : isAuthenticated ? (
          <Box
            sx={{
              display: 'flex',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '4px',
              padding: { xs: '4px 5px', md: '5px 6px' },
              width: { xs: '100%', md: 'auto' },
              minWidth: { xs: '100%', md: '160px' },
            }}
          >
            <UserAvatar compact inline={!isMdUp} />
          </Box>
        ) : (
          <Button
            sx={{
              minHeight: { xs: '32px', md: '40px' },
              height: { xs: '32px', md: '40px' },
              width: { xs: '100%', md: 'auto' },
              fontSize: { xs: '12px', md: '14px' },
              py: { xs: 0, md: undefined },
              textTransform: 'none',
            }}
            variant="contained"
            onClick={() => {
              void signIn(AUTH_PROVIDER, { callbackUrl: pathname });
              onNavigate?.();
            }}
          >
            Sign in
          </Button>
        )}
      </Stack>
    </Toolbar>
  );
}
